import { useEffect, useMemo, useState } from 'react'
import { Col, Label, Row } from 'reactstrap'
import Select from 'react-select'
import Loader from 'react-loaders'
import { request } from '../../../../Redux/Sagas/requests/api'
import { primary_color } from '../../../../Helper/uiHelper'
import ReportEmailSchedulePanel from '../../../Common/ReportEmailSchedule/reportEmailSchedulePanel'
import ReportDownloadButtons from '../../../Common/ReportDownload/reportDownloadButtons'
import './ytdRevenueReport.scss'

const formatMoney = (n) => {
  if (n === null || n === undefined) return ''
  return `£${Number(n).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`
}

const selectStyles = {
  menu: (base) => ({ ...base, zIndex: 20 }),
}

const YtdRevenueReport = ({ cmsKey }) => {
  const currentYear = new Date().getFullYear()
  const [fromYear, setFromYear] = useState(currentYear - 3)
  const [domainIds, setDomainIds] = useState([])
  const [report, setReport] = useState(null)
  const [schools, setSchools] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const yearOptions = useMemo(
    () =>
      Array.from({ length: 11 }, (_, i) => {
        const y = currentYear - i
        return { value: y, label: `${y} (shows ${y}–${currentYear})` }
      }),
    [currentYear]
  )

  const schoolOptions = useMemo(
    () => schools.map((s) => ({ value: String(s.id), label: s.domain_name })),
    [schools]
  )

  const selectedYear = yearOptions.find((o) => o.value === fromYear) || yearOptions[0]
  const selectedSchools = schoolOptions.filter((o) => domainIds.includes(o.value))

  const load = () => {
    setLoading(true)
    setError(null)
    const params = { from_year: fromYear }
    if (domainIds.length) params.domain_ids = domainIds
    request('reports/ytd-revenue', 'GET', {}, {}, params).then((res) => {
      setLoading(false)
      if (res?.status === 200) {
        setReport(res?.data?.data || null)
        setSchools(res?.data?.schools || [])
        return
      }
      setReport(null)
      setError(res?.data?.message || 'Could not load the YTD report. Please refresh the page.')
    })
  }

  useEffect(() => {
    load()
  }, [fromYear, domainIds])

  return (
    <div className='ytd-revenue-report p-3'>
      <Row className='mb-3 align-items-end'>
        <Col md={3}>
          <Label className='subtitle1 font-weight-bold' style={{ color: primary_color }}>
            Search from year
          </Label>
          <Select
            options={yearOptions}
            value={selectedYear}
            onChange={(opt) => setFromYear(opt?.value ?? currentYear - 3)}
            styles={selectStyles}
            isSearchable
            placeholder='Select year'
          />
        </Col>
        <Col md={5}>
          <Label className='subtitle1 font-weight-bold' style={{ color: primary_color }}>
            Schools
          </Label>
          <Select
            options={schoolOptions}
            value={selectedSchools}
            onChange={(opts) => setDomainIds((opts || []).map((o) => o.value))}
            styles={selectStyles}
            isMulti
            isClearable
            isSearchable
            placeholder='All schools'
            closeMenuOnSelect={false}
          />
        </Col>
        <Col md={4} className='d-flex justify-content-end align-items-center flex-wrap'>
          <ReportDownloadButtons
            exportUrl='reports/ytd-revenue/export'
            params={{
              from_year: fromYear,
              ...(domainIds.length ? { domain_ids: domainIds } : {}),
              ...(cmsKey ? { cms_key: cmsKey } : {}),
            }}
            fileBaseName='ytd-revenue-report'
          />
          <ReportEmailSchedulePanel reportKey='ytd_revenue' buttonLabel='Manage emails' />
        </Col>
      </Row>

      {loading ? (
        <div className='d-flex justify-content-center' style={{ minHeight: '12rem', alignItems: 'center' }}>
          <Loader type='ball-pulse' color={primary_color} />
        </div>
      ) : error ? (
        <div className='text-center py-5'>{error}</div>
      ) : !report ? (
        <div className='text-center py-5'>No YTD data to display.</div>
      ) : (
        <div className='table-responsive'>
          <table className='ytd-table'>
            <thead>
              <tr>
                <th className='month-col' rowSpan={2}>
                  Month
                </th>
                {report.years.map((year) => (
                  <th key={year} colSpan={3} className='year-group'>
                    {year}
                  </th>
                ))}
                {report.years?.length >= 2 ? <th rowSpan={2}>YoY £</th> : null}
              </tr>
              <tr>
                {report.years.map((year) => (
                  <FragmentYearHeaders key={`h-${year}`} />
                ))}
              </tr>
            </thead>
            <tbody>
              {report.months.map((row) => (
                <tr key={row.month}>
                  <td className='month-col'>{row.month_label}</td>
                  {report.years.map((year) => {
                    const cell = row.years?.[String(year)] || {}
                    return <FragmentYearCells key={`${row.month}-${year}`} cell={cell} formatMoney={formatMoney} />
                  })}
                  {report.years?.length >= 2 ? (
                    <td>{row.yoy_value_diff !== null && row.yoy_value_diff !== undefined ? formatMoney(row.yoy_value_diff) : ''}</td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

const FragmentYearHeaders = () => (
  <>
    <th>Value</th>
    <th>YTD</th>
    <th>YTD +/-</th>
  </>
)

const FragmentYearCells = ({ cell, formatMoney }) => (
  <>
    <td className={cell?.is_peak ? 'peak-value' : undefined}>
      {cell.value !== null && cell.value !== undefined ? formatMoney(cell.value) : ''}
    </td>
    <td>{cell.ytd !== null && cell.ytd !== undefined ? formatMoney(cell.ytd) : ''}</td>
    <td>{cell.ytd_pct !== null && cell.ytd_pct !== undefined ? `${cell.ytd_pct}%` : ''}</td>
  </>
)

export default YtdRevenueReport
